import { ChangeEvent, FC, useEffect, useMemo, useState } from "react";
import { Flipper } from "react-flip-toolkit";
import { makeStyles } from "tss-react/mui";
import { MiButton, MiSearchInput } from "@miplatform/components";
import { FONT_WEIGHTS } from "@miplatform/constants";
import { ITransferListGroup, ITransferListItem } from "@miplatform/types";
import { groupBy } from "@miplatform/utils";
import {
  Box,
  FormControl,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import {
  ITransferListGroupByOption,
  ITransferListItemsViewProps,
  ITransferListProps,
} from "../TransferList.props";
import { EmptyTransferListView } from "./EmptyTransferListView";
import { TransferListItem } from "./TransferListItem";
import { TransferListLayout } from "./TransferListLayout";

export const TransferList: FC<ITransferListProps> = ({
  availableItems,
  selectedItems,
  onAddItem,
  onRemoveItem,
  availableTitle = "Available",
  selectedTitle = "Selected",
  renderSelectedItemsCount,
  renderAvailableHeaderComponent,
  renderSelectedHeaderComponent,
  renderItemTitle,
  availableSearchPlaceholder = "Search",
  selectedSearchPlaceholder = "Search",
  showAvailableItemsSearch,
  showSelectedItemsSearch,
  allowResorting,
  onSelectedListReorder,
  EmptyAvailableList,
  EmptySelectedList,
  emptyAvailableListMessage = "There are no available items to choose from.",
  emptySelectedListMessage = "Selected items will show here.",
  groupBy,
  groupByMapper,
  getGroupByInfo,
  disabled,
  customAction,
  addDisabled,
  groupBySelectOptions,
}) => {
  const [selectedGroupByOptionId, setSelectedGroupByOptionId] =
    useState<string>(groupBySelectOptions?.[0]?.id ?? "");

  useEffect(() => {
    if (!selectedGroupByOptionId) return;

    const selectedOptionStillExists = !!groupBySelectOptions?.some(
      (option) => option.id === selectedGroupByOptionId
    );

    if (!selectedOptionStillExists) {
      setSelectedGroupByOptionId("");
    }
  }, [groupBySelectOptions, selectedGroupByOptionId]);

  return (
    <TransferListLayout
      availableItems={
        <TransferListItemsView
          items={availableItems}
          onAddItem={disabled ? null : onAddItem}
          showSearch={showAvailableItemsSearch}
          searchPlaceholder={availableSearchPlaceholder}
          excludedItems={selectedItems}
          emptyMessage={emptyAvailableListMessage}
          emptyListComponent={EmptyAvailableList}
          groupBy={groupBy}
          groupByMapper={groupByMapper}
          getGroupByInfo={getGroupByInfo}
          renderItemTitle={renderItemTitle}
          addDisabled={addDisabled}
          groupBySelectOptions={groupBySelectOptions}
          selectedGroupByOptionId={selectedGroupByOptionId}
          onGroupByOptionChange={setSelectedGroupByOptionId}
        />
      }
      selectedItems={
        allowResorting ? (
          <TransferListItemsView
            items={selectedItems}
            onRemoveItem={disabled ? null : onRemoveItem}
            showSearch={showSelectedItemsSearch}
            searchDisabled={disabled}
            allowResorting={allowResorting}
            onSelectedListReorder={onSelectedListReorder}
            emptyMessage={emptySelectedListMessage}
            emptyListComponent={EmptySelectedList}
            renderItemTitle={renderItemTitle}
            searchPlaceholder={selectedSearchPlaceholder}
            groupBySelectOptions={groupBySelectOptions}
            selectedGroupByOptionId={selectedGroupByOptionId}
          />
        ) : (
          <TransferListItemsView
            items={selectedItems}
            onRemoveItem={disabled ? null : onRemoveItem}
            showSearch={showSelectedItemsSearch}
            searchDisabled={disabled}
            emptyMessage={emptySelectedListMessage}
            emptyListComponent={EmptySelectedList}
            groupBy={groupBy}
            groupByMapper={groupByMapper}
            getGroupByInfo={getGroupByInfo}
            renderItemTitle={renderItemTitle}
            searchPlaceholder={selectedSearchPlaceholder}
            customAction={customAction}
            groupBySelectOptions={groupBySelectOptions}
            selectedGroupByOptionId={selectedGroupByOptionId}
          />
        )
      }
      availableTitle={availableTitle}
      selectedTitle={selectedTitle}
      renderAvailableHeaderComponent={renderAvailableHeaderComponent}
      renderSelectedHeaderComponent={renderSelectedHeaderComponent}
      renderSelectedItemsCount={renderSelectedItemsCount}
    />
  );
};

const TransferListItemsView: FC<ITransferListItemsViewProps> = ({
  items,
  excludedItems,
  onAddItem,
  onRemoveItem,
  showSearch,
  searchPlaceholder = "Search...",
  allowResorting,
  onSelectedListReorder,
  emptyMessage,
  emptyListComponent,
  groupBy: groupByProperty,
  groupByMapper = groupBy,
  getGroupByInfo,
  renderItemTitle,
  customAction,
  searchDisabled,
  addDisabled,
  groupBySelectOptions,
  selectedGroupByOptionId,
  onGroupByOptionChange,
}) => {
  const { classes, theme } = useStyles();
  const [search, setSearch] = useState("");

  const getGroupDisplayValue = (
    item: ITransferListItem,
    optionId: string
  ): string => {
    const value = item.groupByOptions?.[optionId];
    return value && value.trim() !== "" ? value : "Other";
  };

  const handleGroupAction = (groupItems: ITransferListItem[]) => {
    if (onAddItem) {
      groupItems.forEach((groupItem) => onAddItem(groupItem.key));
      return;
    }

    if (onRemoveItem) {
      groupItems.forEach((groupItem) => onRemoveItem(groupItem.key));
    }
  };

  const handleReorder = (newItems: ITransferListItem[]) => {
    if (onSelectedListReorder) {
      onSelectedListReorder(newItems);
    }
  };

  const handleReorderUp = (itemKey: string) => {
    const index = items.findIndex((item) => item.key === itemKey);
    if (index !== -1 && index > 0) {
      const newItems = [...items];
      [newItems[index - 1], newItems[index]] = [
        newItems[index],
        newItems[index - 1],
      ];
      handleReorder(newItems);
    }
  };

  const handleReorderDown = (itemKey: string) => {
    const index = items.findIndex((item) => item.key === itemKey);
    if (index !== -1 && index < items.length - 1) {
      const newItems = [...items];
      [newItems[index + 1], newItems[index]] = [
        newItems[index],
        newItems[index + 1],
      ];
      handleReorder(newItems);
    }
  };

  const listItems = useMemo(() => {
    const filteredItems = items.filter((item) => {
      if (!item.key) {
        // prevent items with no key from being shown
        return false;
      }

      if (excludedItems?.some((excluded) => excluded.key === item.key)) {
        return false;
      }

      if (!search || search.trim() === "") {
        return true;
      }

      const searchLower = search.toLowerCase();
      const { title, description, key } = item;
      return [title, description, key].some((field) =>
        field?.toLowerCase().includes(searchLower)
      );
    });

    if (selectedGroupByOptionId) {
      const groupedItems = filteredItems.reduce((acc, item) => {
        const groupName = getGroupDisplayValue(item, selectedGroupByOptionId);
        if (!acc[groupName]) {
          acc[groupName] = [];
        }
        acc[groupName].push(item);
        return acc;
      }, {} as Record<string, ITransferListItem[]>);

      return Object.entries(groupedItems)
        .map(([groupName, groupedGroupItems]) => ({
          key: groupName,
          name: groupName,
          items: groupedGroupItems,
        }))
        .sort((a, b) => (a?.name || "").localeCompare(b?.name || ""));
    }

    if (groupByProperty) {
      const groupedItems: { [key: string]: ITransferListItem[] } =
        groupByMapper(filteredItems, groupByProperty);

      const groupedList = Object.entries(groupedItems).map(
        ([groupKey, items]) => ({
          ...getGroupByInfo?.(groupKey),
          items,
        })
      );

      return groupedList.sort((a, b) =>
        (a?.name || "").localeCompare(b?.name || "")
      );
    }
    return filteredItems;
  }, [
    items,
    excludedItems,
    search,
    selectedGroupByOptionId,
    groupByProperty,
    groupByMapper,
    getGroupByInfo,
  ]);

  const shouldRenderGrouped = Boolean(
    groupByProperty || selectedGroupByOptionId
  );

  const shouldRenderGroupBySelect = Boolean(
    groupBySelectOptions?.length && onAddItem
  );

  return (
    <Box className={classes.transferListItemsViewContainer}>
      {showSearch && (
        <Box className={classes.filtersContainer}>
          <MiSearchInput
            value={search}
            handleChange={(e: ChangeEvent<HTMLInputElement>) =>
              setSearch(e.target.value)
            }
            placeholder={searchPlaceholder}
            style={{
              marginLeft: 0,
              marginBottom: 2,
              flex: 3,
            }}
            textfieldProps={{ style: { height: "auto" } }}
            disabled={searchDisabled}
          />
          {shouldRenderGroupBySelect ? (
            <FormControl size="small" sx={{ flex: 1 }}>
              <TextField
                select
                size="small"
                value={selectedGroupByOptionId || ""}
                onChange={(event) => {
                  if (event.target.value === "None") {
                    // clear value if "None" is selected
                    onGroupByOptionChange?.("");
                    return;
                  }
                  onGroupByOptionChange?.(event.target.value);
                }}
                label="Group By"
                className={classes.groupBySelect}
                disabled={searchDisabled}
              >
                <MenuItem value="None" data-testid="disable-none">
                  None
                </MenuItem>
                {groupBySelectOptions?.map(
                  (option: ITransferListGroupByOption) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.label}
                    </MenuItem>
                  )
                )}
              </TextField>
            </FormControl>
          ) : null}
        </Box>
      )}
      {listItems.length === 0 && emptyListComponent && emptyListComponent}
      {listItems.length === 0 && !emptyListComponent && (
        <EmptyTransferListView message={emptyMessage} />
      )}
      {listItems?.length > 0 && shouldRenderGrouped ? (
        (listItems as ITransferListGroup[]).map((group, groupIndex) => {
          const groupActionLabel = onAddItem
            ? "Select Group"
            : "Deselect Group";

          return (
            <Box key={group.key || groupIndex}>
              <Box className={classes.groupHeaderContainer}>
                {group.name && (
                  <Typography variant="HD2">
                    {group.name}
                    {group.description && (
                      <Typography
                        variant="HD1"
                        fontWeight={FONT_WEIGHTS.REGULAR}
                        color={theme.palette.grey.medium}
                        component="span"
                        pl={theme.spacing(1)}
                      >
                        {` ${group.description}`}
                      </Typography>
                    )}
                  </Typography>
                )}
                {selectedGroupByOptionId && (onAddItem || onRemoveItem) ? (
                  <MiButton
                    size="small"
                    variant="outlined"
                    onClick={() => handleGroupAction(group.items || [])}
                    title={groupActionLabel}
                    disabled={!!onAddItem && addDisabled}
                  />
                ) : null}
              </Box>
              <Box className={classes.transferGroupContainer}>
                {group.items?.map((item: ITransferListItem, index: number) => (
                  <TransferListItem
                    key={item.key}
                    item={item}
                    onAddItem={onAddItem}
                    onRemoveItem={onRemoveItem}
                    itemCount={items.length}
                    index={index}
                    renderItemTitle={renderItemTitle}
                    addDisabled={addDisabled}
                  />
                ))}
              </Box>
            </Box>
          );
        })
      ) : (
        <Flipper
          flipKey={listItems.map((item) => item.key).join("-")}
          className={classes.transferListItemContainer}
        >
          {(listItems as ITransferListItem[]).map((item, index) => (
            <TransferListItem
              key={item.key}
              item={item}
              renderItemTitle={renderItemTitle}
              onAddItem={onAddItem}
              onRemoveItem={onRemoveItem}
              allowResorting={allowResorting}
              onReorderUp={handleReorderUp}
              onReorderDown={handleReorderDown}
              itemCount={items.length}
              index={index}
              customAction={customAction}
              addDisabled={addDisabled}
            />
          ))}
        </Flipper>
      )}
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  transferListItemsViewContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    flex: 1,
  },
  filtersContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    width: "100%",
  },
  groupHeaderContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
  },
  groupBySelect: {
    marginBottom: "1px",
    backgroundColor: theme.palette.background.paper,
  },
  transferGroupContainer: {
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(3),
    gap: theme.spacing(1),
  },
  transferListItemContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
}));
